// ScanCardiograph — batimento (ECG SVG) do scan de segurança.
//
// Enquanto o scan roda, desenha um traçado vivo com UMA batida por check
// concluído, agendada pelos deltas de `ended_at` revelados entre polls (não
// todas de uma vez), comprimindo rajadas num teto de ~1,5s por poll. Entre as
// batidas, respira (nunca achata) — só achata de fato em `status==='failed'`.
// A cor do pico vem da severidade do check e VOLTA ao teal depois (decisão 2);
// check `error` é um blip cinza. Quando o scan já está terminal ao montar,
// pula direto para o veredito (sem replay das batidas passadas).
//
// Reusa as primitivas e a paleta do HardRestart (HeroCardiograph/tokens) para
// não reinventar traçado nem cor fora do design system Amazonas Inovare.

import { useReducedMotion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

import { COLOR } from '../../../recursos/components/HardRestart/tokens'
import type { ScanCheck, ScanCheckStatus } from './types'

const VIEW_WIDTH = 800
const VIEW_HEIGHT = 120
const MID_Y = VIEW_HEIGHT / 2
const TICK_MS = 40
const BUFFER_POINTS = 160
const BEAT_TICKS = 16 // duração do complexo QRS de uma batida
const TERMINAL: ReadonlySet<ScanCheckStatus> = new Set(['pass', 'finding', 'error'])

// ── Cor por batida — volta ao teal (decisão 2) ──────────────────────────

export function beatColorForCheck(check: ScanCheck): string {
  if (check.status === 'error') return COLOR.muted // blip cinza, nem teal nem vermelho
  if (check.status === 'finding') {
    if (check.severity === 'critical' || check.severity === 'high') return COLOR.danger
    if (check.severity === 'medium') return COLOR.warning
    return COLOR.brandVerde // low/info/null → observação, teal
  }
  return COLOR.brandVerde // pass → teal
}

// ── Veredito final (precedência do spec §6.4) ───────────────────────────

export type ScanVerdictKind = 'clean' | 'clean-obs' | 'partial' | 'medium' | 'critical' | 'failed' | 'done'

export interface ScanVerdict {
  kind: ScanVerdictKind
  label: string
  color: string
}

const plural = (n: number, um: string, muitos: string) => `${n} ${n === 1 ? um : muitos}`

export function computeScanVerdict(checks: ScanCheck[], status: string): ScanVerdict | null {
  const terminal = status === 'completed' || status === 'failed'
  if (!terminal) return null
  if (status === 'failed') {
    return { kind: 'failed', label: 'Scan falhou', color: COLOR.danger }
  }
  // Sem registro por check — o progresso não gravou (falha best-effort), ou o
  // /logs veio de uma réplica antiga numa janela de deploy. Não dá para afirmar
  // "limpo" sem evidência; veredito neutro e honesto (nunca verde). (finding #1)
  if (checks.length === 0) {
    return { kind: 'done', label: 'Concluído', color: COLOR.muted }
  }
  let critCount = 0
  let medCount = 0
  let naoAvaliado = 0
  let obsCount = 0
  for (const c of checks) {
    if (c.status === 'error') { naoAvaliado += 1; continue }
    // Check preso em pending/running num scan já completo = desfecho desconhecido
    // (mark_done best-effort pode ter falhado). Conta como não avaliado, nunca
    // como "passou". (finding #1)
    if (c.status === 'pending' || c.status === 'running') { naoAvaliado += 1; continue }
    if (c.status !== 'finding') continue
    if (c.severity === 'critical' || c.severity === 'high') critCount += c.findings_count
    else if (c.severity === 'medium') medCount += c.findings_count
    else obsCount += c.findings_count // low/info/null
  }
  // Precedência: critical/high > medium > não avaliado (parcial) > info/low > limpo.
  if (critCount > 0) {
    return { kind: 'critical', label: `Crítico · ${plural(critCount, 'achado', 'achados')}`, color: COLOR.danger }
  }
  if (medCount > 0) {
    return { kind: 'medium', label: `${plural(medCount, 'achado', 'achados')} de atenção`, color: COLOR.warning }
  }
  if (naoAvaliado > 0) {
    // Âmbar: o scan não falhou, mas ficou incompleto — o operador precisa agir
    // (reexecutar / liberar acesso). Nunca "Limpo".
    return { kind: 'partial', label: `Parcial · ${plural(naoAvaliado, 'não avaliado', 'não avaliados')}`, color: COLOR.warning }
  }
  if (obsCount > 0) {
    return { kind: 'clean-obs', label: `Limpo · ${plural(obsCount, 'observação', 'observações')}`, color: COLOR.brandVerde }
  }
  return { kind: 'clean', label: 'Limpo', color: COLOR.brandVerde }
}

// ── Agenda de batidas (sem replay falso ao recarregar) ─────────────────
//
// Pura e testável. O LiveTrace guarda `seen` (ids já batidos) e `primed`. O
// PRIMEIRO payload não-vazio apenas SEMEIA (marca os já concluídos como vistos,
// sem enfileirar) — assim recarregar no meio do scan não replica o histórico.
// Só os checks que concluírem DEPOIS do prime viram batida. `seen` é mutado.

export function advanceBeatState(
  seen: Set<string>,
  primed: boolean,
  checks: ScanCheck[],
): { primed: boolean; enqueue: ScanCheck[] } {
  const terminais = checks.filter(c => TERMINAL.has(c.status) && c.ended_at)
  if (!primed) {
    if (checks.length === 0) return { primed: false, enqueue: [] } // espera o 1º payload real
    for (const c of terminais) seen.add(c.check_id)
    return { primed: true, enqueue: [] } // semeia, não replica
  }
  const enqueue: ScanCheck[] = []
  for (const c of terminais) {
    if (!seen.has(c.check_id)) { seen.add(c.check_id); enqueue.push(c) }
  }
  return { primed: true, enqueue }
}

// ── Componente ──────────────────────────────────────────────────────────

interface Props {
  checks: ScanCheck[]
  status: string
  height?: number
}

export function ScanCardiograph({ checks, status, height = 120 }: Props) {
  const verdict = computeScanVerdict(checks, status)
  const prefersReduced = useReducedMotion()

  if (verdict) {
    return <VerdictSeal verdict={verdict} height={height} />
  }
  if (prefersReduced) {
    return <ReducedPulse height={height} />
  }
  return <LiveTrace checks={checks} status={status} height={height} />
}

// ── Veredito estático (sem replay ao reabrir) ───────────────────────────

function VerdictSeal({ verdict, height }: { verdict: ScanVerdict; height: number }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        color: verdict.color,
        fontFamily: '"JetBrains Mono", monospace',
      }}
    >
      <span
        aria-hidden="true"
        style={{ width: 12, height: 12, borderRadius: '50%', background: verdict.color }}
      />
      <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '0.01em' }}>{verdict.label}</span>
    </div>
  )
}

// ── Fallback de movimento reduzido ──────────────────────────────────────

function ReducedPulse({ height }: { height: number }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <span
        className="scan-cardio-pulse"
        style={{ width: 24, height: 24, borderRadius: '50%', background: COLOR.brandVerde }}
      />
      <style>{`@keyframes scanCardioPulse{0%,100%{opacity:.5;transform:scale(1)}50%{opacity:1;transform:scale(1.15)}}
        .scan-cardio-pulse{animation:scanCardioPulse 1.5s ease-in-out infinite}
        @media (prefers-reduced-motion: reduce){.scan-cardio-pulse{animation-duration:2.4s}}`}</style>
    </div>
  )
}

// ── Traçado vivo ────────────────────────────────────────────────────────

interface Beat {
  color: string
  gray: boolean // error → blip irregular
  startTick: number
}

function LiveTrace({ checks, status, height }: { checks: ScanCheck[]; status: string; height: number }) {
  const bufferRef = useRef<number[]>(Array(BUFFER_POINTS).fill(MID_Y))
  const colorRef = useRef<string>(COLOR.brandVerde)
  const tickRef = useRef(0)
  const seenRef = useRef<Set<string>>(new Set())
  const queueRef = useRef<ScanCheck[]>([])
  const activeRef = useRef<Beat | null>(null)
  const nextBeatAtRef = useRef(0)
  const primedRef = useRef(false)
  const [, force] = useState(0)

  // A cada atualização dos checks, decide o que SEMEAR vs. ENFILEIRAR. O prime
  // só acontece no 1º payload não-vazio (não no 1º render, que numa recarga vem
  // com checks=[] antes do 1º poll) — por isso recarregar no meio do scan não
  // replica as batidas já passadas. Intervalo derivado do lote (teto ~1,5s/poll).
  useEffect(() => {
    const r = advanceBeatState(seenRef.current, primedRef.current, checks)
    primedRef.current = r.primed
    for (const c of r.enqueue) queueRef.current.push(c)
  }, [checks])

  useEffect(() => {
    let raf = 0
    let last = performance.now()
    const failed = status === 'failed'

    const beatIntervalTicks = () => {
      // Comprime o lote pendente em ~1,5s; piso de ~140ms para a batida ser vista.
      const pend = queueRef.current.length || 1
      const perBeatMs = Math.max(140, Math.min(700, 1500 / pend))
      return Math.round(perBeatMs / TICK_MS)
    }

    const loop = (t: number) => {
      if (t - last >= TICK_MS) {
        last = t
        tickRef.current += 1
        const tick = tickRef.current
        let y = MID_Y

        if (failed) {
          y = MID_Y // flatline só na falha
          colorRef.current = COLOR.danger
        } else {
          // Começa uma batida se chegou a hora e há check na fila.
          if (!activeRef.current && queueRef.current.length > 0 && tick >= nextBeatAtRef.current) {
            const c = queueRef.current.shift() as ScanCheck
            activeRef.current = { color: beatColorForCheck(c), gray: c.status === 'error', startTick: tick }
            colorRef.current = activeRef.current.color
            nextBeatAtRef.current = tick + beatIntervalTicks()
          }
          const beat = activeRef.current
          if (beat) {
            const local = tick - beat.startTick
            y = MID_Y - (beat.gray ? grayBlip(local) : qrs(local))
            if (local >= BEAT_TICKS) {
              activeRef.current = null
              colorRef.current = COLOR.brandVerde // volta ao teal entre batidas
            }
          } else {
            y = MID_Y - 3 * Math.sin(tick * 0.12) // respiração
          }
        }

        const buf = bufferRef.current
        buf.shift()
        buf.push(y)
        force(n => (n + 1) & 0xff)
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [status])

  const path = bufferToPath(bufferRef.current)
  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <line x1={0} x2={VIEW_WIDTH} y1={MID_Y} y2={MID_Y}
            stroke={colorRef.current} strokeWidth={0.5} strokeDasharray="2 4" opacity={0.2} />
      <path d={path} fill="none" stroke={colorRef.current}
            strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

// Complexo QRS de uma batida (0..BEAT_TICKS). Pico nítido.
function qrs(t: number): number {
  if (t < 2) return 3 * (t / 2)
  if (t < 4) return 3 - 2 * (t - 2) // P
  if (t < 6) return -6 * (t - 4) // Q
  if (t < 8) return -12 + 40 * (t - 6) // R sobe
  if (t < 10) return 68 - 38 * (t - 8) // R desce
  if (t < 12) return -8 // S
  if (t < 16) return 5 * Math.sin(((t - 12) / 4) * Math.PI) // T
  return 0
}

// Blip irregular (error): pequeno e trêmulo, sem pico limpo.
function grayBlip(t: number): number {
  if (t > 10) return 0
  return (6 + 3 * Math.sin(t * 1.7)) * Math.sin(t * 0.9)
}

function bufferToPath(buf: number[]): string {
  if (buf.length === 0) return ''
  const dx = VIEW_WIDTH / (buf.length - 1)
  let d = `M 0 ${buf[0].toFixed(2)}`
  for (let i = 1; i < buf.length; i++) d += ` L ${(i * dx).toFixed(2)} ${buf[i].toFixed(2)}`
  return d
}
